import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import db from '../db/db'
import routes from '../pages/routes'
import {
  findExistingBookings,
  officeDesks,
  refreshBookingsForDate,
  sessionState,
  loadTeamMembers
} from '../state'
import Database from '../types/Database'
import { getPreviousOrCurrentSunday } from '../utils/calendarHelpers'
import { generateDateRange } from '../utils/dateRangeHelpers'
import { createDateOnly, formatDateToString } from '../utils/FormatHelpers'

export interface TeamMemberOption {
  userId: string
  name: string
  email: string
}

/**
 * useBookingForm - Custom hook for managing booking form state and logic
 *
 * Handles all booking-related state management, form validation,
 * and business logic for the booking process.
 */
export function useBookingForm() {
  const navigate = useNavigate()
  const [userState, setUserState] = useRecoilState(sessionState)
  const [busy, setBusy] = React.useState<boolean>(false)
  const [selectedDesks, setSelectedDesks] = React.useState<
    Database.DeskWithAvailability[]
  >([])
  const [notifyUser, setNotifyUser] = React.useState<string>('')
  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false)

  // Team member state
  const [teamMembers, setTeamMembers] = React.useState<TeamMemberOption[]>([])
  const [selectedBookingOwnerId, setSelectedBookingOwnerId] =
    React.useState<string>('')

  // Load team members on mount
  React.useEffect(() => {
    const fetchTeamMembers = async () => {
      if (!userState.user?.uid) {
        return
      }

      // Default to current user
      setSelectedBookingOwnerId(userState.user.uid)

      try {
        const members = await loadTeamMembers(userState.user.uid)

        // Load user details for each team member
        const teamMemberOptions: TeamMemberOption[] = []
        for (const member of members) {
          // Get the other user's ID (not the current user)
          const otherUserId =
            member.userId1 === userState.user.uid
              ? member.userId2
              : member.userId1

          try {
            // Query by uid field instead of using getById (which uses document ID)
            const userDocs = await db.query({
              collection: 'users',
              where: ['uid', '==', otherUserId]
            })
            if (userDocs.length > 0) {
              const userDoc = userDocs[0]
              const name =
                `${userDoc.firstname || ''} ${userDoc.lastname || ''}`.trim() ||
                userDoc.email ||
                'Unknown User'
              teamMemberOptions.push({
                userId: otherUserId,
                name,
                email: userDoc.email || ''
              })
            }
          } catch (error) {
            console.error(
              `Error loading user details for ${otherUserId}:`,
              error
            )
          }
        }

        setTeamMembers(teamMemberOptions)
      } catch (error) {
        console.error('Error loading team members:', error)
      }
    }

    fetchTeamMembers()
  }, [userState.user?.uid])

  const handleOfficeChange = async (e: any) => {
    if (!userState.offices) return
    const selectedOffice = userState.offices.filter(
      x => x.oid === e.target.value
    )[0]
    const newOfficeDesks = await officeDesks(selectedOffice)

    setUserState(old => ({
      ...old,
      selectedOffice: selectedOffice,
      officeDesks: newOfficeDesks
    }))

    setSelectedDesks([])
  }

  const handleUnitChange = async (e: any) => {
    const selectedUnit = e.target.value

    setUserState(old => ({
      ...old,
      selectedRecurUnit: selectedUnit,
      // Reset week days when switching modes
      selectedWeekDays: selectedUnit === 'Same days every week' ? [] : []
    }))

    const dateRangeBookings = await findExistingBookings(
      new Date(userState.selectedFromDate),
      userState.selectedRecurAmount,
      selectedUnit,
      userState.offices ?? [],
      userState.selectedWeekDays
    )

    setUserState(old => ({
      ...old,
      bookings: dateRangeBookings,
      selectedRecurUnit: selectedUnit
    }))
  }

  const handleWeekDaysChange = (days: number[]) => {
    setUserState(old => ({
      ...old,
      selectedWeekDays: days
    }))
  }

  const handleFromDayChange = async (e: any) => {
    refreshBookingsForDate(
      setUserState,
      userState,
      new Date(e.currentTarget.value)
    )
  }

  const setNewAmountValue = async (num: number) => {
    if (Number.isNaN(num) || num < 1) return

    setUserState(old => ({
      ...old,
      selectedRecurAmount: num
    }))

    const dateRangeBookings = await findExistingBookings(
      new Date(userState.selectedFromDate),
      num,
      userState.selectedRecurUnit,
      userState.offices ?? [],
      userState.selectedWeekDays
    )

    setUserState(old => ({
      ...old,
      selectedRecurAmount: num,
      bookings: dateRangeBookings
    }))
  }

  const handleDeskClicked = (desk: Database.DeskWithAvailability) => {
    if (!desk.isAvailable) return

    // Toggle desk selection - if already selected, remove it; otherwise, select it
    setSelectedDesks(prevSelected => {
      const isAlreadySelected = prevSelected.some(
        selectedDesk =>
          selectedDesk.office === desk.office && selectedDesk.name === desk.name
      )

      if (isAlreadySelected) {
        // Remove from selection
        return prevSelected.filter(
          selectedDesk =>
            !(
              selectedDesk.office === desk.office &&
              selectedDesk.name === desk.name
            )
        )
      } else {
        // Add to selection (for now, only allow one desk at a time)
        return [desk]
      }
    })
  }

  const getAvailableDesks = (): Database.DeskWithAvailability[] => {
    if (!userState.officeDesks) return []

    const datesInRange = generateDateRange(
      new Date(userState.selectedFromDate),
      userState.selectedRecurUnit,
      userState.selectedRecurAmount,
      userState.selectedWeekDays
    )

    return userState.officeDesks.map(desk => {
      const hasConflictingBooking = userState.bookings?.find(
        booking =>
          booking.did === desk.did &&
          desk.office === booking.oid &&
          datesInRange.find(
            d =>
              formatDateToString(d) ===
              formatDateToString(booking.forDate ?? new Date())
          )
      )

      const hasAccess =
        !desk.availableTo ||
        desk.availableTo.includes(userState.user?.email ?? '')
      const isAvailable = !hasConflictingBooking && hasAccess

      const conflictingBooking = userState.bookings?.find(
        booking =>
          booking.did === desk.did &&
          desk.office === booking.oid &&
          datesInRange.find(
            d =>
              formatDateToString(d) ===
              formatDateToString(booking.forDate ?? new Date())
          )
      )

      return {
        ...desk,
        isAvailable,
        conflictingBooking
      }
    })
  }

  const deleteBooking = async (deleteDate: Date) => {
    const dbooking = await db.query({
      collection: 'booking',
      where: [
        ['uid', '==', userState.user?.uid],
        ['forDate', '==', deleteDate]
      ]
    })
    await db.deleteItem('booking', dbooking[0].id)

    refreshBookingsForDate(
      setUserState,
      userState,
      new Date(userState.selectedFromDate)
    )
  }

  const confirmBookDesk = async () => {
    if (selectedDesks.length === 0) {
      setNotifyUser('No desk selected')
      return
    }
    // Show confirmation for weekly bookings or multiple day bookings
    const isWeekly = userState.selectedRecurUnit === 'Same days every week'
    const isMultiDay = userState.selectedRecurAmount > 1

    if (isWeekly || isMultiDay) {
      setConfirmOpen(true)
    } else {
      await bookDesks(selectedBookingOwnerId)
    }
  }

  const bookDesks = async (bookingOwnerId?: string) => {
    setBusy(true)
    const newBookingsList: Database.Booking[] = []

    // Determine the owner and creator IDs
    const ownerId = bookingOwnerId || userState.user?.uid
    const creatorId = userState.user?.uid
    const isProxy = ownerId !== creatorId

    for (const desk of selectedDesks) {
      let numOfNewBookings = 0
      let numExistingBookings = 0

      // Determine booking dates based on mode
      const bookingDates: Date[] = []

      if (userState.selectedRecurUnit === 'Once off') {
        // Single day booking for each duration day
        for (let index = 0; index < userState.selectedRecurAmount; index++) {
          const forDate = createDateOnly(new Date(userState.selectedFromDate))
          forDate.setDate(forDate.getDate() + index)
          bookingDates.push(forDate)
        }
      } else {
        // Weekly recurring bookings for 3 months (approximately 13 weeks)
        const startDate = createDateOnly(new Date(userState.selectedFromDate))
        const weeksToBook = 13 // 3 months

        for (let week = 0; week < weeksToBook; week++) {
          for (const dayOfWeek of userState.selectedWeekDays) {
            const bookingDate = new Date(startDate)
            // Calculate days to add: (week * 7) + offset to reach the target day of week
            const startDayOfWeek = startDate.getDay()
            let daysToAdd = dayOfWeek - startDayOfWeek
            if (daysToAdd < 0) daysToAdd += 7 // If target day is before start day, go to next week
            daysToAdd += week * 7

            bookingDate.setDate(startDate.getDate() + daysToAdd)
            bookingDates.push(bookingDate)
          }
        }
      }

      // Create bookings for all calculated dates
      for (const forDate of bookingDates) {
        const keyDate = forDate.toISOString().slice(0, 10).replace(/-/g, '')

        const existingBooking = userState.bookings?.find(b => {
          // Check if the booking owner (not the current user) already has a booking on this date
          const bookingOwnerId = b.bookingOwnerId || b.uid
          return (
            bookingOwnerId === ownerId &&
            b.forDate &&
            formatDateToString(b.forDate) === formatDateToString(forDate)
          )
        })

        if (existingBooking) {
          numExistingBookings++
        } else {
          // Get the owner's name for bookerName field
          let ownerName =
            userState.user?.firstname +
            ' ' +
            userState.user?.lastname?.substring(0, 1)
          if (isProxy && ownerId) {
            // Find the team member's name
            const teamMember = teamMembers.find(tm => tm.userId === ownerId)
            if (teamMember) {
              // Extract first name and last initial from team member
              const nameParts = teamMember.name.split(' ')
              ownerName =
                nameParts[0] +
                (nameParts.length > 1
                  ? ' ' + nameParts[nameParts.length - 1]?.substring(0, 1)
                  : '')
            }
          }

          const newBooking: Database.Booking = {
            bookingKey: keyDate + '-' + desk.office + '-' + desk.name,
            id: keyDate + '-' + desk.office + '-' + desk.name,
            officeBookingKey: keyDate + '-' + desk.office,
            deskName: desk.name,
            did: desk.did,
            officeName: userState.selectedOffice?.name ?? '',
            oid: desk.office,
            forDate: forDate,
            uid: ownerId,
            appver: 2,
            bookerName: ownerName,
            bookingOwnerId: ownerId,
            bookingCreatorId: creatorId,
            isProxyBooking: isProxy
          }
          newBookingsList.push(newBooking)
          await db.addItem('booking', newBooking)
          numOfNewBookings++
        }
      }

      const currentBookings = userState.bookings ? [...userState.bookings] : []
      const currentUserBookings = userState.userBookings
        ? [...userState.userBookings]
        : []
      setUserState(old => ({
        ...old,
        bookings: currentBookings.concat(newBookingsList),
        userBookings: currentUserBookings.concat(newBookingsList)
      }))

      // Get the owner's name for the success message
      let ownerName = userState.user?.firstname || 'User'
      if (isProxy) {
        // Find the team member's name
        const teamMember = teamMembers.find(tm => tm.userId === ownerId)
        ownerName = teamMember?.name || 'team member'
      }

      const existNote =
        numExistingBookings > 0
          ? 'Could not add ' +
            numExistingBookings +
            ' as ' +
            (isProxy ? ownerName : 'you') +
            ' already ' +
            (isProxy ? 'has' : 'have') +
            ' a desk booked on that date.'
          : ''

      const successMessage = isProxy
        ? `Booked ${numOfNewBookings} desk${
            numOfNewBookings > 1 ? 's' : ''
          } for ${ownerName}. ${existNote}`
        : `Added ${numOfNewBookings} booking${
            numOfNewBookings > 1 ? 's' : ''
          }. ${existNote}`

      setNotifyUser(successMessage)
      setConfirmOpen(false)
    }
    setBusy(false)

    // Navigate back to calendar after successful booking
    if (newBookingsList.length > 0) {
      // Collect all the dates that were booked
      const bookedDates = newBookingsList.map(booking =>
        formatDateToString(booking.forDate ?? new Date())
      )

      setTimeout(() => {
        // Calculate the Sunday before or on the booking date
        const bookingDate = new Date(userState.selectedFromDate)
        const sunday = getPreviousOrCurrentSunday(bookingDate)

        // Update the state to show the week containing the booking
        // and set the highlighted dates
        setUserState(old => ({
          ...old,
          selectedFromDate: formatDateToString(sunday),
          selectedRecurAmount: 35,
          highlightedDates: bookedDates
        }))

        navigate(routes.home)
      }, 1500)
    }
  }

  return {
    userState,
    busy,
    selectedDesks,
    notifyUser,
    confirmOpen,
    teamMembers,
    selectedBookingOwnerId,
    handleOfficeChange,
    handleUnitChange,
    handleFromDayChange,
    setNewAmountValue,
    handleDeskClicked,
    getAvailableDesks,
    deleteBooking,
    confirmBookDesk,
    bookDesks,
    setNotifyUser,
    setConfirmOpen,
    setSelectedBookingOwnerId,
    handleWeekDaysChange
  }
}
